import { z } from 'zod';

/**
 * Mini-game runs contract (WP1 implementuje, WP0 zamraża).
 *
 * SwimRace / button-mash (PLAN_REVIEW2 korekta 3 + §14.5):
 *  - Serwer CLAMP-uje tempo tapów do MAX_TAPS_PER_S (NIE odrzuca wyniku).
 *  - Czas liczony z ZEGARA SERWERA (now - started_at), NIE ufamy durationMs klienta.
 *  - durationMs w stats jest informacyjne; serwer używa serverDurationMs.
 */
export const MAX_TAPS_PER_S = 12 as const;
export const SERVER_DURATION_TOLERANCE = 0.2 as const; // ±20% vs client report (telemetry only)

export const GameIdSchema = z.enum([
  'rhythm',
  'cooking',
  'runner',
  'bubbles',
  'puzzle',
  'swimrace',
]);
export type GameId = z.infer<typeof GameIdSchema>;

export const RunStartRequestSchema = z.object({
  gameId: GameIdSchema,
});
export type RunStartRequest = z.infer<typeof RunStartRequestSchema>;

export const RunStartResponseSchema = z.object({
  runId: z.string().min(1),
});
export type RunStartResponse = z.infer<typeof RunStartResponseSchema>;

export const RunStatsSchema = z.object({
  stars: z.number().int().nonnegative(),
  score: z.number().int().nonnegative(),
  /**
   * Informacyjne — klient może podać, ale serwer NIE ufa tej wartości.
   * Autorytatywny czas = now - runs.started_at (zegar serwera).
   */
  durationMs: z.number().int().nonnegative().optional(),
  /**
   * Liczba tapów (button-mash). Serwer clamp-uje taps/s do MAX_TAPS_PER_S:
   * effectiveTaps = min(taps, floor(serverDurationSec * MAX_TAPS_PER_S)).
   * Wynik NIGDY nie jest odrzucany z powodu zbyt szybkich tapów.
   */
  taps: z.number().int().nonnegative().optional(),
});
export type RunStats = z.infer<typeof RunStatsSchema>;

export const RunFinishRequestSchema = z.object({
  runId: z.string().min(1),
  stats: RunStatsSchema,
});
export type RunFinishRequest = z.infer<typeof RunFinishRequestSchema>;

export const RunFinishResponseSchema = z.object({
  rewardZl: z.number().int().nonnegative(),
  balance: z.number().int().nonnegative(),
  stars: z.number().int().nonnegative(),
  /** Czas z zegara serwera (ms) — źródło prawdy. */
  serverDurationMs: z.number().int().nonnegative(),
  /** Tapy po clampie serwera (jeśli podano taps). */
  effectiveTaps: z.number().int().nonnegative().optional(),
  /** true gdy serwer ograniczył tempo (clamp, nie deny). */
  tapsClamped: z.boolean().optional(),
});
export type RunFinishResponse = z.infer<typeof RunFinishResponseSchema>;

/**
 * Czysta reguła clampu — używana przez WP1 i testy kontraktowe.
 * Serwer ZAWSZE akceptuje finish; nadmiarowe tapy są obcinane.
 */
export function clampTaps(
  taps: number,
  serverDurationMs: number,
  maxTapsPerS: number = MAX_TAPS_PER_S,
): { effectiveTaps: number; tapsClamped: boolean } {
  const sec = Math.max(serverDurationMs / 1000, 0.001);
  const cap = Math.floor(sec * maxTapsPerS);
  const effectiveTaps = Math.min(Math.max(0, Math.floor(taps)), cap);
  return { effectiveTaps, tapsClamped: effectiveTaps < taps };
}
